import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CardTitle } from "./CardTitle";
import { CardSubtitle } from "./CardSubtitle";

export const Card = ({ children, title, subtitle, isAccordion, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggleAccordion = useCallback(() => {
    setExpanded(prevState => !prevState);
  }, []);

  const showSubtitle = subtitle && (!isAccordion || expanded);

  return (
    <Box
      sx={{
        display: "flex",
        padding: {
          xs: isAccordion ? "16px" : "12px 12px 16px",
          sm: "30px"
        },
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "32px",
        alignSelf: "stretch",
        borderRadius: "16px",
        backgroundColor: "background.paper",
        mt: {
          xs: 2,
          sm: 4
        }
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: {
            xs: "24px",
            sm: "32px"
          }
        }}
      >
        {(title || showSubtitle || isAccordion) && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            {(title || showSubtitle) && (
              <Box
                component={isAccordion ? "button" : "div"}
                {...(isAccordion ? { type: "button" } : {})}
                onClick={isAccordion ? toggleAccordion : undefined}
                sx={{
                  cursor: isAccordion ? "pointer" : "default",
                  flexGrow: 1,
                  "&:hover": {
                    opacity: isAccordion ? 0.8 : 1
                  },
                  // Reset button styles if it's a button
                  border: isAccordion ? "none" : undefined,
                  background: "transparent",
                  padding: 0,
                  textAlign: "left",
                  width: "100%"
                }}
                aria-expanded={isAccordion ? expanded : undefined}
              >
                {title && <CardTitle>{title}</CardTitle>}
                {showSubtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
              </Box>
            )}
            {isAccordion && (
              <IconButton
                onClick={toggleAccordion}
                aria-expanded={expanded}
                aria-label={expanded ? "Collapse" : "Expand"}
                sx={{ mt: -1, mr: -1 }}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Box>
        )}
        <Box
          sx={{
            display: !isAccordion || expanded ? "flex" : "none",
            flexDirection: "column",
            width: "100%",
            gap: {
              xs: "24px",
              sm: "32px"
            }
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

Card.propTypes = {
  children: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  isAccordion: PropTypes.bool,
  defaultExpanded: PropTypes.bool
};
